import {
  ADD_MESSAGE,
  UPDATE_MESSAGE,
  DELETE_MESSAGE,
  CLEAR_MESSAGES,
  SET_MESSAGES,
  MIGRATE_SESSION_MESSAGES,
  UPDATE_STREAM_DATA,
  CLEAR_STREAM_DATA,
  SET_LOADING
} from '../actions/types'

const initialState = {
  messages: {}, // keyed by message ID
  messagesBySession: {}, // keyed by session ID, values are arrays of message IDs
  streamData: {}, // current stream data for active research
  loading: false
}

export default function messageReducer(state = initialState, action) {
  switch (action.type) {
    case ADD_MESSAGE: {
      const message = action.payload
      const messageId = message.id || Date.now().toString()
      const sessionId = message.sessionId
      
      console.log('📝 ADD_MESSAGE reducer:', {
        messageId,
        sessionId,
        role: message.role,
        contentLength: message.content?.length || 0,
        existingSessionMessages: state.messagesBySession[sessionId]?.length || 0
      })
      
      const newState = {
        ...state,
        messages: {
          ...state.messages,
          [messageId]: { ...message, id: messageId }
        },
        messagesBySession: {
          ...state.messagesBySession,
          [sessionId]: [
            ...(state.messagesBySession[sessionId] || []),
            messageId
          ]
        }
      }
      
      console.log('✅ Message added. Session now has', newState.messagesBySession[sessionId]?.length || 0, 'messages')
      
      return newState
    }
    
    case UPDATE_MESSAGE: {
      const { messageId, updates } = action.payload
      
      console.log('🔄 UPDATE_MESSAGE reducer:', {
        messageId,
        updates: Object.keys(updates),
        messageExists: !!state.messages[messageId],
        contentLength: updates.content?.length || 0
      })
      
      if (!state.messages[messageId]) {
        console.log('❌ Message not found for update:', messageId)
        return state
      }
      
      const updatedMessage = {
        ...state.messages[messageId],
        ...updates,
        updatedAt: new Date().toISOString()
      }
      
      console.log('✅ Message updated:', messageId, 'isStreaming:', updatedMessage.isStreaming, 'content length:', updatedMessage.content?.length || 0)
      
      return {
        ...state,
        messages: {
          ...state.messages,
          [messageId]: updatedMessage
        }
      }
    }
    
    case DELETE_MESSAGE: {
      const messageId = action.payload
      const message = state.messages[messageId]
      
      if (!message) {
        return state
      }
      
      const { [messageId]: deletedMessage, ...remainingMessages } = state.messages
      const sessionMessages = state.messagesBySession[message.sessionId] || []
      
      return {
        ...state,
        messages: remainingMessages,
        messagesBySession: {
          ...state.messagesBySession,
          [message.sessionId]: sessionMessages.filter(id => id !== messageId)
        }
      }
    }
    
    case CLEAR_MESSAGES: {
      const sessionId = action.payload
      
      if (sessionId) {
        // Clear messages for specific session
        const sessionMessages = state.messagesBySession[sessionId] || []
        const remainingMessages = { ...state.messages }
        
        sessionMessages.forEach(messageId => {
          delete remainingMessages[messageId]
        })
        
        return {
          ...state,
          messages: remainingMessages,
          messagesBySession: {
            ...state.messagesBySession,
            [sessionId]: []
          }
        }
      } else {
        // Clear all messages
        return {
          ...state,
          messages: {},
          messagesBySession: {}
        }
      }
    }
    
    case SET_MESSAGES: {
      const messages = action.payload
      const newMessages = {}
      const newMessagesBySession = {}
      
      messages.forEach(message => {
        const messageId = message.id
        newMessages[messageId] = message
        
        if (!newMessagesBySession[message.sessionId]) {
          newMessagesBySession[message.sessionId] = []
        }
        newMessagesBySession[message.sessionId].push(messageId)
      })
      
      return {
        ...state,
        messages: newMessages,
        messagesBySession: newMessagesBySession
      }
    }
    
    case MIGRATE_SESSION_MESSAGES: {
      const { oldSessionId, newSessionId, messageIds } = action.payload
      
      console.log('🔄 MIGRATE_SESSION_MESSAGES:', {
        oldSessionId,
        newSessionId,
        messageIds,
        oldSessionMessages: state.messagesBySession[oldSessionId]?.length || 0,
        newSessionMessages: state.messagesBySession[newSessionId]?.length || 0
      })
      
      // Update sessionId for all specified messages
      const updatedMessages = { ...state.messages }
      messageIds.forEach(messageId => {
        if (updatedMessages[messageId]) {
          updatedMessages[messageId] = {
            ...updatedMessages[messageId],
            sessionId: newSessionId
          }
        }
      })
      
      // Update messagesBySession mapping
      const updatedMessagesBySession = { ...state.messagesBySession }
      
      // Remove messages from old session
      if (updatedMessagesBySession[oldSessionId]) {
        updatedMessagesBySession[oldSessionId] = updatedMessagesBySession[oldSessionId].filter(
          id => !messageIds.includes(id)
        )
      }
      
      // Add messages to new session
      if (!updatedMessagesBySession[newSessionId]) {
        updatedMessagesBySession[newSessionId] = []
      }
      updatedMessagesBySession[newSessionId] = [
        ...updatedMessagesBySession[newSessionId],
        ...messageIds
      ]
      
      console.log('✅ Migration complete:', {
        oldSessionMessages: updatedMessagesBySession[oldSessionId]?.length || 0,
        newSessionMessages: updatedMessagesBySession[newSessionId]?.length || 0
      })
      
      return {
        ...state,
        messages: updatedMessages,
        messagesBySession: updatedMessagesBySession
      }
    }
    
    case UPDATE_STREAM_DATA: {
      const { phase, data } = action.payload
      
      console.log('🗂️ UPDATE_STREAM_DATA reducer processing:', {
        phase,
        data,
        dataKeys: data ? Object.keys(data) : [],
        currentStreamData: state.streamData,
        currentStreamDataKeys: Object.keys(state.streamData)
      })
      
      // For agent_progress, merge data instead of replacing to handle reconnection duplicates
      if (phase === 'agent_progress') {
        const newState = {
          ...state,
          streamData: {
            ...state.streamData,
            [phase]: {
              ...state.streamData[phase],
              ...data
            }
          }
        }
        console.log('🗂️ Updated streamData (agent_progress):', newState.streamData)
        return newState
      }
      
      const newState = {
        ...state,
        streamData: {
          ...state.streamData,
          [phase]: data
        }
      }
      console.log('🗂️ Updated streamData (general):', newState.streamData)
      return newState
    }
    
    case CLEAR_STREAM_DATA:
      return {
        ...state,
        streamData: {}
      }
    
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload
      }
      
    default:
      return state
  }
}